// Sales Brain V9 — print-friendly documents (browser Print → Save as PDF).
// Route: /studio/docs/:leadId/:docType?lang=en|cn|bi
// Docs: audit (1 page) · creative (storyboard) · proposal (MAX 2 pages) · quotation.
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { studioHeaders, isStudioAuthed } from "@/lib/studioAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIT = { primary: "#2F3A28", accent: "#7A8450", background: "#FBF8F2", text: "#26211A", muted: "#8A8069" };

export default function DocPrint() {
    const { leadId, docType } = useParams();
    const [sp] = useSearchParams();
    const lang = ["en", "cn", "bi"].includes(sp.get("lang")) ? sp.get("lang") : "bi";
    const [doc, setDoc] = useState(null);
    const [quote, setQuote] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (!isStudioAuthed()) { setErr("请先在 /studio 登录后再打开此页面"); return; }
        (async () => {
            try {
                const { data } = await axios.get(`${API}/sales-brain/leads/${leadId}`, { headers: studioHeaders() });
                setDoc(data);
                if (docType === "quotation" || docType === "proposal") {
                    const q = await axios.get(`${API}/sales-brain/leads/${leadId}/quote`, { headers: studioHeaders() });
                    setQuote(q.data.quote);
                }
            } catch { setErr("加载失败 — 请确认已登录 Studio"); }
        })();
    }, [leadId, docType]);

    if (err) return <div style={{ padding: 60, fontFamily: "sans-serif" }}>{err}</div>;
    if (!doc) return <div style={{ padding: 60, fontFamily: "sans-serif", color: "#888" }}>Loading…</div>;

    const vars = {
        "--bk-primary": KIT.primary, "--bk-accent": KIT.accent, "--bk-bg": KIT.background,
        "--bk-text": KIT.text, "--bk-muted": KIT.muted,
    };
    const ctx = { doc, quote, lang };
    return (
        <div style={{ ...vars, background: "var(--bk-bg)", color: "var(--bk-text)", minHeight: "100vh", fontFamily: "'Manrope', sans-serif" }} data-testid="doc-print">
            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    .pbreak { break-before: page; }
                    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    div[style*="border-radius"] { break-inside: avoid; page-break-inside: avoid; }
                    tr { break-inside: avoid; }
                    .doc-label, h1, h2 { break-after: avoid-page; page-break-after: avoid; }
                }
                .doc-h { font-family: 'Playfair Display', Georgia, serif; letter-spacing: 0.01em; font-weight: 500; }
                .doc-label { font-size: 10px; letter-spacing: 0.24em; text-transform: uppercase; color: var(--bk-accent); font-weight: 700; }
            `}</style>
            <button onClick={() => window.print()} className="no-print" data-testid="doc-print-btn"
                style={{ position: "fixed", top: 18, right: 18, zIndex: 50, background: "var(--bk-primary)", color: "var(--bk-bg)", border: "none", borderRadius: 999, padding: "10px 22px", fontSize: 13, cursor: "pointer", boxShadow: "0 4px 14px rgba(0,0,0,.2)" }}>
                🖨 Print / Save as PDF
            </button>
            {docType === "audit" && <AuditDoc {...ctx} />}
            {docType === "creative" && <CreativeDoc {...ctx} />}
            {docType === "proposal" && <ProposalDoc {...ctx} />}
            {docType === "quotation" && <QuotationDoc {...ctx} />}
        </div>
    );
}

// ---------- shared ----------
const hasCJK = (s) => /[\u4e00-\u9fff]/.test(s || "");
const enOnly = (s) => typeof s === "string"
    ? s.replace(/[\u3000-\u303f\u3400-\u4dbf\u4e00-\u9fff\uff01-\uff5e]+/g, " ").replace(/\s{2,}/g, " ").trim()
    : s;
function pickLang(en, zh) {
    let e = en || "", z = zh || "";
    if (hasCJK(e) && z && !hasCJK(z)) { const t = e; e = z; z = t; }
    return [e, z];
}
function useT(lang) {
    return (obj, key) => {
        const [en, zh] = pickLang(obj?.[`${key}_en`], obj?.[`${key}_cn`]);
        if (lang === "en") return enOnly(en || zh);
        if (lang === "cn") return zh || en;
        if (en && zh && en !== zh) return <span>{en}<span style={{ display: "block", opacity: 0.78, marginTop: 3 }}>{zh}</span></span>;
        return en || zh;
    };
}
function biItem(lang, x) {
    const [en, zh] = pickLang(x?.en, x?.cn);
    if (lang === "en") return enOnly(en || zh);
    if (lang === "cn") return zh || en;
    if (en && zh && en !== zh) return <span>{en}<span style={{ display: "block", opacity: 0.78, marginTop: 2 }}>{zh}</span></span>;
    return en || zh;
}
function Page({ children, breakBefore = true, style }) {
    return <section className={breakBefore ? "pbreak" : ""} style={{ maxWidth: 760, margin: "0 auto", padding: "56px 48px", ...style }}>{children}</section>;
}
function H({ label, title }) {
    return (
        <header style={{ marginBottom: 26 }}>
            <p className="doc-label">{label}</p>
            <h2 className="doc-h" style={{ fontSize: 30, margin: "8px 0 0", lineHeight: 1.2 }}>{title}</h2>
            <div style={{ width: 44, height: 2, background: "var(--bk-accent)", marginTop: 14 }} />
        </header>
    );
}
function Foot({ lang }) {
    return (
        <div style={{ marginTop: 46, paddingTop: 14, borderTop: "1px solid rgba(127,127,127,.25)", display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--bk-muted)" }}>
            <span>Kaily F&B Visual Studio — slowcafe.my</span>
            <span>{new Date().toLocaleDateString(lang === "en" ? "en-MY" : "zh-CN", { year: "numeric", month: "long", day: "numeric" })}</span>
        </div>
    );
}
const RATING_LABELS = [["brand", "Brand", "品牌"], ["visual", "Visual", "视觉"],
["content", "Content", "内容"], ["trust", "Trust", "信任"], ["cta", "CTA", "行动号召"]];
const rateLabel = (lang, en, zh) => (lang === "en" ? en : lang === "cn" ? zh : `${en} · ${zh}`);
const StarRow = ({ v }) => (
    <span style={{ color: "var(--bk-accent)", fontSize: 18, letterSpacing: 4 }}>{"★".repeat(v || 0)}{"☆".repeat(Math.max(0, 5 - (v || 0)))}</span>
);

// ---------- ① Lean Audit — 5 star ratings + top 5 problems (1 page) ----------
function AuditDoc({ doc, lang }) {
    const au = doc.audit || {}, r = au.ratings || {}, sn = doc.snapshot || {};
    const T = useT(lang);
    return (
        <Page breakBefore={false} style={{ paddingTop: 64 }}>
            <p className="doc-label">Kaily F&B Visual Studio · {rateLabel(lang, "Instagram Audit", "Instagram 审计")}</p>
            <h1 className="doc-h" style={{ fontSize: 42, margin: "12px 0 4px" }}>{doc.business_name}</h1>
            <p style={{ fontSize: 13, color: "var(--bk-muted)", margin: 0 }}>{T(sn, "industry")}{sn.location ? ` · ${sn.location}` : ""}</p>

            <div style={{ marginTop: 36 }}>
                <p className="doc-label" style={{ marginBottom: 14 }}>{rateLabel(lang, "5 Ratings", "五项评分")}</p>
                <div style={{ display: "grid", gap: 12 }}>
                    {RATING_LABELS.map(([k, en, zh]) => (
                        <div key={k} style={{ display: "flex", alignItems: "center", gap: 16, fontSize: 14 }}>
                            <span style={{ width: 150, color: "var(--bk-muted)" }}>{rateLabel(lang, en, zh)}</span>
                            <StarRow v={r[k]} />
                            <b style={{ fontSize: 13 }}>{r[k]}/5</b>
                        </div>))}
                </div>
            </div>

            <div style={{ marginTop: 36 }}>
                <p className="doc-label" style={{ marginBottom: 14 }}>{rateLabel(lang, "Top 5 Problems", "五大问题")}</p>
                <div style={{ display: "grid", gap: 12 }}>
                    {(au.problems || []).map((p, i) => (
                        <div key={i} style={{ display: "flex", gap: 12, fontSize: 14, lineHeight: 1.6 }}>
                            <span className="doc-h" style={{ color: "var(--bk-accent)", width: 22, flexShrink: 0 }}>{i + 1}.</span>
                            <span style={{ flex: 1 }}>{biItem(lang, p)}</span>
                        </div>))}
                </div>
            </div>

            {doc.pain && (
                <p style={{ marginTop: 34, fontSize: 15, lineHeight: 1.7, fontWeight: 600, padding: 18, borderLeft: "3px solid var(--bk-accent)", background: "rgba(127,127,127,.06)" }}>
                    🔥 {rateLabel(lang, "Biggest pain: ", "最大痛点：")}{biItem(lang, doc.pain)}</p>)}
            <Foot lang={lang} />
        </Page>
    );
}

// ---------- ② Creative Direction — 6 videos + free teaser ----------
function CreativeDoc({ doc, lang }) {
    const cr = doc.creative;
    const T = useT(lang);
    if (!cr) return <Page breakBefore={false}><p>Storyboard 还没生成。</p></Page>;
    const ds = doc.doc_settings || {};
    const inc = ds.videos_included;
    const videos = (cr.videos || []).filter((v) => !inc || inc.includes(v.no));
    const ft = cr.free_teaser;
    const Scene = ({ s }) => (
        <div style={{ borderRadius: 10, border: "1px solid rgba(127,127,127,.2)", padding: "10px 14px", fontSize: 12.5 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                <b style={{ color: "var(--bk-accent)" }}>S{s.no}</b>
                <span style={{ flex: 1 }}>{biItem(lang, { en: s.shot_en, cn: s.shot_cn })}</span>
                {s.duration && <span style={{ fontSize: 11, color: "var(--bk-muted)", flexShrink: 0 }}>⏱ {s.duration}</span>}
            </div>
            <div style={{ display: "flex", gap: 18, fontSize: 11.5, color: "var(--bk-muted)", marginTop: 3 }}>
                {s.lens && <span>🔭 {s.lens}</span>}{s.movement && <span>↝ {s.movement}</span>}
            </div>
            {(s.voiceover_en || s.voiceover_cn) && <p style={{ margin: "3px 0 0", fontSize: 11.5 }}>🎙 {biItem(lang, { en: s.voiceover_en, cn: s.voiceover_cn })}</p>}
        </div>
    );
    return (
        <div>
            <Page breakBefore={false} style={{ paddingTop: 56 }}>
                <p className="doc-label">Kaily F&B Visual Studio · {rateLabel(lang, "Creative Direction", "创意方向")}</p>
                <h1 className="doc-h" style={{ fontSize: 38, margin: "12px 0 6px" }}>{T(cr, "title")}</h1>
                <p style={{ fontSize: 13, color: "var(--bk-muted)", margin: 0 }}>{doc.business_name}</p>
                <p style={{ marginTop: 22, fontSize: 14, lineHeight: 1.7, padding: 14, borderLeft: "3px solid var(--bk-accent)", background: "rgba(127,127,127,.06)" }}>
                    🤖 {T(cr, "ai_suggestion")}</p>
                {cr.my_idea && (
                    <p style={{ marginTop: 10, fontSize: 13, lineHeight: 1.7, padding: 12, borderLeft: "3px solid var(--bk-primary)", background: "rgba(127,127,127,.04)" }}>
                        ✍️ {rateLabel(lang, "My idea (merged): ", "我的想法（已合并）：")}{cr.my_idea}</p>)}
                {ft && (
                    <div style={{ marginTop: 20, borderRadius: 12, border: "2px solid var(--bk-accent)", padding: 18 }}>
                        <p className="doc-label" style={{ marginBottom: 8 }}>🎁 {rateLabel(lang, "Free 15s teaser — shot on the spot", "免费 15 秒试拍 — 到店当场拍")}</p>
                        <p style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 600 }}>{biItem(lang, { en: ft.concept_en, cn: ft.concept_cn })}</p>
                        <div style={{ display: "grid", gap: 5 }}>
                            {(ft.shots || []).map((s, i) => <p key={i} style={{ margin: 0, fontSize: 12.5 }}>🎬 {biItem(lang, s)}</p>)}
                        </div>
                        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--bk-muted)", fontStyle: "italic" }}>💬 {biItem(lang, { en: ft.pitch_en, cn: ft.pitch_cn })}</p>
                    </div>)}
                <div style={{ display: "grid", gap: 16, marginTop: 26 }}>
                    {videos.map((v) => (
                        <div key={v.no} style={{ borderRadius: 12, border: "1px solid rgba(127,127,127,.25)", padding: 18 }}>
                            <p className="doc-h" style={{ color: "var(--bk-accent)", fontSize: 19, margin: 0 }}>
                                Video {v.no} — {biItem(lang, { en: v.title_en, cn: v.title_cn })}</p>
                            <p style={{ margin: "6px 0 2px", fontSize: 13 }}><b>{rateLabel(lang, "Purpose", "目的")}: </b>{biItem(lang, { en: v.purpose_en, cn: v.purpose_cn })}</p>
                            {(v.hook_en || v.hook_cn) && <p style={{ margin: "0 0 10px", fontSize: 12.5, color: "var(--bk-muted)" }}>🪝 {biItem(lang, { en: v.hook_en, cn: v.hook_cn })}</p>}
                            <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
                                {(v.scenes || []).map((s) => <Scene key={s.no} s={s} />)}
                            </div>
                        </div>))}
                </div>
                {(cr.scenes || []).length > 0 && !cr.videos && (
                    <div style={{ display: "grid", gap: 10, marginTop: 24 }}>
                        {(cr.scenes || []).map((s) => <Scene key={s.no} s={s} />)}
                    </div>)}
                <Foot lang={lang} />
            </Page>
        </div>
    );
}

// ---------- ③ Proposal — MAX 2 pages ----------
function ProposalDoc({ doc, quote, lang }) {
    const T = useT(lang);
    const ds = doc.doc_settings || {};
    const showAi = ds.show_ai_system !== false;
    const prop = (!showAi && doc.proposal_alt) ? doc.proposal_alt : doc.proposal;
    const p1 = prop?.page1 || {}, p2 = prop?.page2 || {};
    const menuShoot = ds.include_menu_shoot ? doc.menu_shoot_reco : null;
    const showPrices = ds.show_prices !== false;
    const partnerLogo = ds.show_partner_logo ? ds.partner_logo_url : null;
    const inc = ds.videos_included;
    const contentPlan = ((doc.creative || {}).videos || []).filter((v) => !inc || inc.includes(v.no));
    const q = quote || {};
    const items = showPrices ? (q.items || []).filter((it) => it.enabled !== false) : [];
    const Label = ({ children, mt = 0 }) => <p className="doc-label" style={{ margin: `${mt}px 0 10px` }}>{children}</p>;
    return (
        <div>
            {/* PAGE 1 — summary · challenges · solutions · investment */}
            <Page breakBefore={false} style={{ paddingTop: 48 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
                    <span className="doc-h" style={{ fontSize: 20 }}>kaily<span style={{ color: "var(--bk-accent)" }}>.</span> <span style={{ fontSize: 11, fontFamily: "'Manrope', sans-serif", color: "var(--bk-muted)", letterSpacing: "0.08em" }}>F&B VISUAL STUDIO</span></span>
                    {partnerLogo && (
                        <>
                            <span style={{ color: "var(--bk-muted)", fontSize: 16 }}>×</span>
                            <img src={partnerLogo} alt="partner" style={{ height: 40, width: 40, borderRadius: 10, objectFit: "cover" }} data-testid="doc-partner-logo" />
                        </>)}
                </div>
                <p className="doc-label">{rateLabel(lang, "Growth Proposal", "增长提案")}</p>
                <h1 className="doc-h" style={{ fontSize: 36, margin: "10px 0 20px" }}>{doc.business_name}</h1>
                <p style={{ fontSize: 15.5, lineHeight: 1.75, borderLeft: "3px solid var(--bk-accent)", paddingLeft: 16, margin: 0 }}>
                    {T(p1, "exec_summary")}</p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28, marginTop: 30 }}>
                    <div>
                        <Label>{rateLabel(lang, "3 Current Challenges", "3 大现状挑战")}</Label>
                        <div style={{ display: "grid", gap: 10 }}>
                            {(p1.challenges || []).map((c, i) => (
                                <div key={i} style={{ display: "flex", gap: 8, fontSize: 13.5, lineHeight: 1.55 }}>
                                    <span style={{ color: "#B0503C", fontWeight: 700 }}>✗</span><span>{biItem(lang, c)}</span></div>))}
                        </div>
                    </div>
                    <div>
                        <Label>{rateLabel(lang, "3 Solutions", "3 大解决方案")}</Label>
                        <div style={{ display: "grid", gap: 10 }}>
                            {(p1.solutions || []).map((s, i) => (
                                <div key={i} style={{ display: "flex", gap: 8, fontSize: 13.5, lineHeight: 1.55 }}>
                                    <span style={{ color: "var(--bk-accent)", fontWeight: 700 }}>✓</span><span>{biItem(lang, s)}</span></div>))}
                        </div>
                    </div>
                </div>
                {items.length > 0 && showPrices && (
                    <div style={{ marginTop: 32, padding: 20, borderRadius: 14, border: "2px solid var(--bk-accent)" }}>
                        <Label>{rateLabel(lang, "Investment", "投资")}</Label>
                        {items.map((it, i) => (
                            <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, padding: "4px 0" }}>
                                <span>{it.name}{it.qty > 1 ? ` × ${it.qty}` : ""}</span>
                                <span>RM {(it.qty * it.unit_price).toLocaleString()}</span>
                            </div>))}
                        {Number(q.discount) > 0 && (
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, padding: "4px 0", color: "#B0503C" }}>
                                <span>{rateLabel(lang, "Discount", "折扣")}</span><span>− RM {Number(q.discount).toLocaleString()}</span></div>)}
                        <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid rgba(127,127,127,.3)", marginTop: 8, paddingTop: 10 }}>
                            <b>{rateLabel(lang, "Total", "总计")}</b>
                            <span className="doc-h" style={{ fontSize: 22, color: "var(--bk-primary)" }}>RM {Number(q.total || 0).toLocaleString()}</span>
                        </div>
                        <p style={{ margin: "6px 0 0", fontSize: 12, color: "var(--bk-muted)", textAlign: "right" }}>
                            {lang === "en" ? `${q.deposit_pct || 50}% deposit to confirm: ` : `订金 ${q.deposit_pct || 50}% 确认档期：`}RM {Number(q.deposit || 0).toLocaleString()}</p>
                    </div>)}
            </Page>
            {/* PAGE 2 — content plan · timeline · deliverables · expected results */}
            <Page>
                <H label="Page 2" title={rateLabel(lang, "Plan & Results", "执行计划与成果")} />
                {contentPlan.length > 0 && (
                    <div style={{ marginBottom: 26 }}>
                        <Label>{rateLabel(lang, "Content Plan", "内容企划")}</Label>
                        <div style={{ display: "grid", gap: 8 }}>
                            {contentPlan.map((v) => (
                                <div key={v.no} style={{ display: "flex", gap: 12, fontSize: 13, alignItems: "baseline" }}>
                                    <span className="doc-h" style={{ color: "var(--bk-accent)", width: 32, flexShrink: 0 }}>V{v.no}</span>
                                    <span><b>{biItem(lang, { en: v.title_en, cn: v.title_cn })}</b> — {biItem(lang, { en: v.purpose_en, cn: v.purpose_cn })}</span>
                                </div>))}
                        </div>
                    </div>)}
                {menuShoot && (
                    <div style={{ marginBottom: 26, padding: 16, borderRadius: 12, border: "1px solid var(--bk-accent)" }}>
                        <Label>📷 {biItem(lang, { en: menuShoot.title_en, cn: menuShoot.title_cn }) || rateLabel(lang, "Product & Menu Shoot", "产品 / 菜单拍摄")}</Label>
                        <div style={{ display: "grid", gap: 8 }}>
                            {(menuShoot.points || []).map((pt, i) => (
                                <div key={i} style={{ display: "flex", gap: 10, fontSize: 13 }}>
                                    <span style={{ color: "var(--bk-accent)", fontWeight: 700 }}>✓</span><span>{biItem(lang, pt)}</span></div>))}
                        </div>
                    </div>)}
                <Label>{rateLabel(lang, "Timeline", "时间线")}</Label>
                <div style={{ display: "grid", gap: 8, marginBottom: 28 }}>
                    {(p2.timeline || []).map((w, i) => (
                        <div key={i} style={{ display: "flex", gap: 14, fontSize: 13.5, alignItems: "baseline" }}>
                            <span className="doc-h" style={{ color: "var(--bk-accent)", width: 40, flexShrink: 0 }}>W{w.week}</span>
                            <span>{biItem(lang, { en: w.title_en, cn: w.title_cn })}</span>
                        </div>))}
                </div>
                <Label>{rateLabel(lang, "Deliverables", "交付物")}</Label>
                <div style={{ display: "grid", gap: 8, marginBottom: 28 }}>
                    {(p2.deliverables || []).map((d, i) => (
                        <div key={i} style={{ display: "flex", gap: 10, fontSize: 13.5 }}>
                            <span style={{ color: "var(--bk-accent)", fontWeight: 700 }}>✓</span><span>{biItem(lang, d)}</span></div>))}
                </div>
                <Label>{rateLabel(lang, "Expected Results", "预期成果")}</Label>
                <div style={{ display: "grid", gap: 10 }}>
                    {(p2.expected_results || []).map((r, i) => (
                        <div key={i} style={{ display: "flex", gap: 10, fontSize: 14, fontWeight: 600 }}>
                            <span style={{ color: "var(--bk-accent)" }}>★</span><span>{biItem(lang, r)}</span></div>))}
                </div>
                <Foot lang={lang} />
            </Page>
        </div>
    );
}

// ---------- ④ Quotation ----------
function QuotationDoc({ doc, quote, lang }) {
    const T = useT(lang);
    const q = quote || {};
    const items = (q.items || []).filter((it) => it.enabled !== false);
    return (
        <div>
            <Page breakBefore={false} style={{ paddingTop: 72 }}>
                <p className="doc-label">Kaily F&B Visual Studio · {rateLabel(lang, "Quotation", "报价单")}</p>
                <h1 className="doc-h" style={{ fontSize: 40, margin: "12px 0 4px" }}>{doc.business_name}</h1>
                <p style={{ fontSize: 12.5, color: "var(--bk-muted)" }}>{new Date().toLocaleDateString("en-MY", { year: "numeric", month: "long", day: "numeric" })} · Valid 14 days</p>
                <table style={{ width: "100%", marginTop: 32, borderCollapse: "collapse", fontSize: 13.5 }} data-testid="doc-quote-table">
                    <thead>
                        <tr style={{ borderBottom: "2px solid var(--bk-primary)" }}>
                            <th style={{ textAlign: "left", padding: "10px 6px" }}>{lang === "en" ? "Item" : "项目"}</th>
                            <th style={{ textAlign: "right", padding: "10px 6px", width: 50 }}>{lang === "en" ? "Qty" : "数量"}</th>
                            <th style={{ textAlign: "right", padding: "10px 6px", width: 100 }}>{lang === "en" ? "Unit" : "单价"}</th>
                            <th style={{ textAlign: "right", padding: "10px 6px", width: 110 }}>{lang === "en" ? "Amount" : "小计"}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((it, i) => (
                            <tr key={i} style={{ borderBottom: "1px solid rgba(127,127,127,.18)" }}>
                                <td style={{ padding: "12px 6px" }}>
                                    <div style={{ fontWeight: 600 }}>{it.name}</div>
                                    {it.description && <div style={{ fontSize: 11.5, color: "var(--bk-muted)", marginTop: 2 }}>{it.description}</div>}
                                </td>
                                <td style={{ textAlign: "right", padding: "12px 6px" }}>{it.qty}</td>
                                <td style={{ textAlign: "right", padding: "12px 6px" }}>RM {Number(it.unit_price).toLocaleString()}</td>
                                <td style={{ textAlign: "right", padding: "12px 6px" }}>RM {(it.qty * it.unit_price).toLocaleString()}</td>
                            </tr>
                        ))}
                        {Number(q.discount) > 0 && (
                            <tr>
                                <td colSpan={3} style={{ textAlign: "right", padding: "10px 6px", color: "var(--bk-muted)" }}>{rateLabel(lang, "Discount", "折扣")}</td>
                                <td style={{ textAlign: "right", padding: "10px 6px", color: "#B0503C" }}>− RM {Number(q.discount).toLocaleString()}</td>
                            </tr>
                        )}
                        <tr style={{ borderTop: "2px solid var(--bk-primary)" }}>
                            <td colSpan={3} style={{ textAlign: "right", padding: "12px 6px", fontWeight: 700 }}>{rateLabel(lang, "Grand Total", "总计")}</td>
                            <td className="doc-h" style={{ textAlign: "right", padding: "12px 6px", fontSize: 19, color: "var(--bk-primary)" }}>RM {Number(q.total || 0).toLocaleString()}</td>
                        </tr>
                        <tr>
                            <td colSpan={3} style={{ textAlign: "right", padding: "6px", color: "var(--bk-muted)", fontSize: 12.5 }}>{lang === "en" ? `Deposit ${q.deposit_pct || 50}% to confirm` : `订金 ${q.deposit_pct || 50}% 确认档期`}</td>
                            <td style={{ textAlign: "right", padding: "6px", fontWeight: 700, color: "var(--bk-accent)" }}>RM {Number(q.deposit || 0).toLocaleString()}</td>
                        </tr>
                    </tbody>
                </table>
                {T(q, "payment_terms") && (
                    <div style={{ marginTop: 30, fontSize: 13, lineHeight: 1.7 }}>
                        <p className="doc-label" style={{ marginBottom: 6 }}>{lang === "en" ? "Payment terms" : "付款条款"}</p>
                        <p style={{ margin: 0 }}>{T(q, "payment_terms")}</p>
                    </div>)}
            </Page>
            <Page>
                <H label={lang === "en" ? "Acceptance" : "确认"} title={rateLabel(lang, "Acceptance", "确认与签署")} />
                <p style={{ fontSize: 13.5, lineHeight: 1.75, color: "var(--bk-muted)" }}>
                    {lang === "en"
                        ? "By signing below, you accept this quotation and authorise Kaily F&B Visual Studio to reserve the production dates. A 50% deposit confirms the booking."
                        : lang === "cn"
                            ? "签署即表示接受本报价，并授权 Kaily F&B Visual Studio 保留档期。支付 50% 订金即确认预订。"
                            : "By signing below, you accept this quotation and authorise Kaily F&B Visual Studio to reserve the production dates. · 签署即表示接受本报价，支付 50% 订金即确认预订。"}
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, marginTop: 72 }}>
                    {[[rateLabel(lang, "Client", "客户")], ["Kaily F&B Visual Studio"]].map(([who], i) => (
                        <div key={i}>
                            <div style={{ borderBottom: "1px solid var(--bk-text)", height: 48 }} />
                            <p style={{ fontSize: 12, marginTop: 8 }}>{who}</p>
                            <p style={{ fontSize: 11, color: "var(--bk-muted)", marginTop: 24 }}>{rateLabel(lang, "Date", "日期")}: ____________</p>
                        </div>
                    ))}
                </div>
            </Page>
        </div>
    );
}
